<template>
  <div class="service-form-wrapper">
    <div class="service-form-container">
      <div class="sticky-action-buttons">
        <div class="sticky-buttons-container px-4 md:px-0">
          <div class="flex items-center gap-2 md:gap-4 overflow-hidden">
            <vs-button type="button" @click="handleBackClick" class="text-black font-bold cancel-btn">
              Cancel
            </vs-button>
            <span class="service-name-display truncate max-w-[150px] md:max-w-none" v-if="wizardTitle">{{ wizardTitle
            }}</span>
          </div>
          <div class="flex items-center gap-2 md:gap-4">
            <vs-button type="button" @click="handleSaveDraft" :disabled="submittingService" color="secondary"
              size="large" class="save-draft-btn vs-con-loading__container" id="save-draft-btn">
              Save as draft
            </vs-button>
            <vs-button type="button" @click="handlePublish" :disabled="submittingService" color="primary" size="large"
              class="publish-btn vs-con-loading__container" id="publish-btn">
              {{ submittingService ? 'Publishing...' : 'Publish' }}
            </vs-button>
          </div>
        </div>
      </div>

      <div class="form-content-scrollable">
        <div class="form-content">
          <div class="mb-6">
            <h2 class="service-heading-primary font-bold">Basic Details</h2>
          </div>

          <form id="submit-service-form" @submit.prevent="handleSubmit">
            <!-- Step 1 Content: Basic Information -->
            <div class="mb-8">

              <div class="vx-row mb-6">
                <div class="vx-col md:w-1/2 w-full my-3">
                  <p class="mb-2 text-md font-bold">Service Name *</p>
                  <select-2 class="w-full " name="service" placeholder="Select Service" :value="selectedServiceId"
                    v-model="selectedServiceId" @input="(item) => (selectedServiceId = item && item.value)"
                    :options="serviceList" autocomplete :ssr="false" :multiple="false" :viewRendered="false"
                    :disabled="isEditMode" :clearable="!isEditMode" />
                  <span v-if="step1Errors.service" class="text-danger text-sm">{{ step1Errors.service }}</span>
                  <div v-if="servicesLoading" class="text-sm text-gray-500 mt-1">Loading services...</div>
                  <div v-if="!servicesLoading && serviceList.length === 0" class="text-sm text-red-500 mt-1">
                    {{ isEditMode ? 'No services available' : 'No services available. All services may already be created for this gurudwara.' }}
                  </div>
                </div>
              </div>

              <div class="vx-row mb-6">
                <div class="vx-col w-full mb-4 mt-4">
                  <p class="mb-2 text-md font-bold">Service Image *</p>

                  <div class="file-upload-wrapper">
                    <label for="service-image-input" class="file-upload-box w-full md:max-w-md">
                      <input id="service-image-input" type="file" ref="serviceImageInput" @change="handleImageUpload"
                        accept="image/png,image/jpeg,image/jpg" class="file-input-hidden" />
                      <div class="file-upload-content-new">
                        <feather-icon icon="UploadIcon" :svgClasses="['w-6', 'h-6', 'text-black']" />
                        <span class="file-upload-text-new">Browse files to upload image</span>
                      </div>
                    </label>
                    <div v-if="serviceImagePreview || existingServiceImage" class="service-image-preview">
                      <img :src="serviceImagePreview || existingServiceImage" alt="Service Image Preview" />
                      <button type="button" @click="removeImage" class="remove-image-btn">
                        <feather-icon icon="XIcon" :svgClasses="['w-4', 'h-4']" />
                      </button>
                    </div>
                  </div>

                  <div class="file-upload-note">
                    <p class="file-upload-note-text">Please note - Only 1 image can be uploaded. File size should be
                      less than 1 MB. Only PNG and JPG format is supported.</p>
                  </div>

                  <span v-if="step1Errors.serviceImage" class="text-danger text-sm mt-2 block">{{
                    step1Errors.serviceImage }}</span>


                </div>
              </div>


              <div class="vx-col md:w-1/1 w-full md:mt-8 mb-6">
                <div>
                  <p class="mb-2 text-md font-bold">Service Locations Allowed *</p>
                  <div class="border border-gray-200 rounded-lg px-4 py-4">
                    <div class="flex flex-wrap items-center gap-4 md:gap-6">
                      <div v-for="option in serviceLocationOptions" :key="option.value" :class="[
                        'service-location-card border rounded-lg p-4 md:p-6 cursor-pointer selected ',
                        isServiceLocationSelected(option.value) ? 'selected' : ''
                      ]" @click="toggleServiceLocation(option.value)">
                        <div class="flex items-center gap-3">
                          <span :class="[
                            'service-location-checkbox flex w-6 h-6 rounded ',
                            isServiceLocationSelected(option.value) ? 'selected' : ''
                          ]">
                            <feather-icon v-if="isServiceLocationSelected(option.value)" icon="CheckIcon"
                              :svgClasses="['w-5', 'h-5']" />
                          </span>
                          <span class="font-medium service-form-label">{{ option.label }}</span>
                        </div>
                      </div>
                    </div>
                    <p class="inline-block bg-green-90 rounded px-3 py-1 mt-6" style="color:#021C02; font-size:17px;">
                      {{ serviceLocationSelection.length }} option{{ serviceLocationSelection.length === 1 ? '' : 's' }}
                      selected
                    </p>
                  </div>
                  <span v-if="step1Errors.locations" class="text-danger text-sm mt-2 block">{{ step1Errors.locations
                  }}</span>
                </div>
              </div>


              <div class="vx-row mb-6">
                <div class="vx-col w-full">
                  <p class="mb-2 text-md font-bold">Mobile booking — date &amp; time</p>
                  <p class="text-sm text-gray-600 mb-3">Controls what customers see in the Sangat app when booking this service.</p>
                  <div class="flex flex-col gap-3">
                    <vs-checkbox v-model="requiresBookingDate" @input="onRequiresBookingDateChange">
                      Require customer to select a date
                    </vs-checkbox>
                    <vs-checkbox v-model="requiresTimeSlot" :disabled="!requiresBookingDate">
                      Require customer to select a time slot
                    </vs-checkbox>
                  </div>
                </div>
              </div>

              <div class="vx-col w-full mt-3">
                <div class="demo-alignment">
                  <p class="mb-2 text-md font-bold">Service Description *</p>
                  <vs-textarea v-model="textarea" placeholder="Write brief about the service" height="150px" />
                  <span v-if="step1Errors.description" class="text-danger text-sm">{{ step1Errors.description }}</span>
                </div>
              </div>

              <div class="bg-gray-400 h-1 opacity-25 w-full my-10"></div>


              <div class="vx-col w-full mb-6">
                <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <p class="service-heading-secondary font-bold mb-0">Which Months Is This Service Available?</p>
                  <div class="flex items-center gap-3">
                    <span class="text-base font-semibold">Select all</span>
                    <vs-switch :value="allMonthsSelected" @input="setAllMonths" />
                  </div>
                </div>
                <div class="months-grid">
                  <div class="demo-alignment border rounded-lg border-solid border-gray-300 month-card"
                    v-for="(month, index) in months" :key="index">
                    <div class="flex items-center gap-3 px-4 py-3">
                      <vs-switch v-model="month.value">
                        <span slot="on">On</span>
                        <span slot="off">Off</span>
                      </vs-switch>
                      <label class="text-base">{{ month.name }}</label>
                    </div>
                  </div>
                </div>
                <p class="inline-block bg-green-90  rounded px-3 py-1 mt-6" style="color:#021C02; font-size:17px;">
                  {{ selectedMonthsCount }} month{{ selectedMonthsCount === 1 ? '' : 's' }} selected
                </p>
                <span v-if="step1Errors.months" class="text-danger text-sm ml-2">{{ step1Errors.months }}</span>
              </div>
            </div>

            <div class="bg-gray-400 h-1 opacity-25 w-full my-10"></div>

            <!-- Step 2 Content: Operating Days & Hours -->
            <div class="mb-8">
              <div class="days-hours-header flex flex-wrap items-center justify-between gap-4 mb-4">
                <h3 class="service-heading-primary font-bold">What Days & Hours Is This Service Available?</h3>
                <div class="flex items-center gap-x-4 shrink-0">
                  <vs-switch v-model="is24hOpen" @change="handle24hToggle" color="success" class="ml-1"></vs-switch>
                  <strong class="vs-input--label">Open All Day (24/7)</strong>
                </div>
              </div>

              <div class="operating-days-container">
                <div v-for="(day, index) in days" :key="day.name" class="day-row-wrapper">
                  <div :class="['operating-day-card', { 'has-error': dayErrors[index] }]">
                    <div class="day-card-body">

                      <div class="day-header">
                        <div class="day-toggle-section">
                          <vs-switch v-model="day.enabled" @input="handleDayToggle(index, $event)"
                            :disabled="is24hOpen" />

                          <label class="day-label">{{ day.label }}</label>
                        </div>
                      </div>

                      <div class="day-card-main">
                        <div v-if="!day.enabled">
                          <h5 class="unavailable-text">Not Available To Book</h5>
                        </div>

                        <div v-else class="day-content">
                          <!-- Time Intervals -->
                          <div class="intervals-container">
                            <div v-for="(interval, i) in day.intervals" :key="i" class="interval-row-group">
                              <div class="interval-row">
                                <div class="time-input-wrapper">
                                  <flat-pickr :config="configdateTimePicker" v-model="interval.start"
                                    placeholder="Start Time" class="time-input time-input-start"
                                    :disabled="is24hOpen" />
                                </div>
                                <span class="time-separator">-</span>
                                <div class="time-input-wrapper">
                                  <flat-pickr :config="configdateTimePicker" v-model="interval.end"
                                    placeholder="End Time" class="time-input time-input-end" :disabled="is24hOpen" />
                                </div>
                              </div>
                              <button v-if="day.intervals.length > 1 && !is24hOpen" type="button"
                                class="remove-interval-btn" @click.prevent="removeInterval(index, i)">
                                <feather-icon icon="XIcon" :svgClasses="['w-6', 'h-6']" class="mr-1"
                                  style="font-size:18px;" /> Remove
                              </button>
                            </div>
                            <!-- Add New Interval - Hide if 24h open -->
                            <a v-if="day.intervals.length > 0 && !is24hOpen" class="add-interval-link"
                              @click.prevent="addInterval(index)">
                              + Add New Interval
                            </a>
                          </div>

                          <!-- Validation Messages -->
                          <div v-if="day.enabled && day.intervals.length > 0" class="validation-messages">
                            <div v-if="dayErrors[index]" class="error-message-box">
                              <feather-icon icon="AlertCircleIcon" :svgClasses="['w-6', 'h-6', 'error-icon']" />
                              <span>{{ dayErrors[index] }}</span>
                            </div>
                            <div v-else-if="day.intervals.length > 1 && !dayErrors[index]" class="success-message-box">
                              <feather-icon icon="CheckIcon" :svgClasses="['w-6', 'h-6', 'success-icon']" />
                              <span>No duplicate time intervals</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>

                  <!-- Copy to All Days Button - Always render container for alignment, conditionally show content -->
                  <div class="copy-section-outside">
                    <!-- Copy button - Only show for this specific day when it has no errors -->
                    <button v-if="!dayErrors[index] && day.enabled && !is24hOpen" type="button" class="copy-to-all-btn"
                      @click="copyToAllDays(index)">
                      <feather-icon icon="CopyIcon" :svgClasses="['w-6', 'h-6']" />
                      <span>Copy to all days</span>
                    </button>
                    <!-- Error message when this specific day has errors -->
                    <div v-else-if="dayErrors[index]" class="copy-error-message">
                      <feather-icon icon="AlertCircleIcon" :svgClasses="['w-6', 'h-6']" />
                      <span>First fix errors, then <br /> you can copy to all days.</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="bg-gray-400 h-1 opacity-25 w-full my-10"></div>
              <p class="mb-4 service-heading-primary font-bold">Which Time Slot Can Customer Book?</p>
              <div class="vx-col option-cards-grid mb-6">
                <div class="vx-col w-full">
                  <div
                    :class="['option-choice-card border border-gray-300 border-solid rounded cursor-pointer', intervalMode === 'flexible' ? 'shadow' : '']"
                    @click="intervalMode = 'flexible'">
                    <div class="flex items-start gap-3">
                      <vs-radio v-model="intervalMode" vs-name="slot_timing" vs-value="flexible"></vs-radio>
                      <div>
                        <h4 class="mb-2">Flexible. Let customer choose.</h4>
                        <p class="text-gray-600 service-text-base">Best for services like Anand Karaj. Where a person
                          can pick and choose slots between operating hours.</p>
                        <span v-if="intervalMode === 'flexible'" class="inline-block bg-green-90 rounded mt-3"
                          style="padding:5px 10px; font-size:17px; color:#021C02;">selected</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="vx-col w-full">
                  <div
                    :class="['option-choice-card border border-gray-300 border-solid rounded cursor-pointer', intervalMode === 'fixed' ? 'shadow' : '']"
                    @click="intervalMode = 'fixed'">
                    <div class="flex items-start gap-3">
                      <vs-radio v-model="intervalMode" vs-name="slot_timing" vs-value="fixed"></vs-radio>
                      <div>
                        <h4 class="mb-2">Fixed as defined by you.</h4>
                        <p class="text-gray-600 text-base">Best for services like Punjabi language teaching classes or
                          Pagri teaching classes. Where a person has to choose a defined fixed time only.</p>
                        <span v-if="intervalMode === 'fixed'" class="inline-block bg-green-90 rounded mt-3"
                          style="padding:5px 10px; font-size:17px; color:#021C02;">selected</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="bg-gray-400 h-1 opacity-25 w-full my-10"></div>
              <p class="mb-4 service-heading-primary font-bold">How Many Maximum Customer Bookings You Want Per Time
                Slot?</p>
              <div class="vx-row items-start mb-6 settings-row">
                <div class="vx-col w-full md:w-2/3">
                  <h5 class="mb-2.5 font-medium" style="font-size:20px;">How many bookings per time slot?</h5>
                  <p class=" text-base" style="color:#666666;">Example - Punjabi teaching classes can have 20 bookings
                    per time slot whereas Anand Karaj could have 1 booking allowed per available time slot.</p>
                </div>
                <div class="vx-col w-full md:w-1/3 flex md:justify-end mt-4 md:mt-0">
                  <vs-input placeholder="Enter Amount" type="number" min="1" v-model.number="maxBookingsPerSlot"
                    class="settings-input" />
                </div>
              </div>

              <div class="bg-gray-400 h-1 opacity-25 w-full my-10"></div>
              <p class="mb-4 service-heading-primary font-bold">What Amount Will Customer Pay For Booking?</p>
              <div class="vx-col option-cards-grid mb-6">
                <div class="vx-col w-full">
                  <div
                    :class="['option-choice-card border border-gray-300 border-solid rounded cursor-pointer', payment_option === 'fixed' ? 'shadow' : '']"
                    @click="payment_option = 'fixed'">
                    <div class="flex items-start gap-3">
                      <vs-radio v-model="payment_option" vs-name="option_for_payment" vs-value="fixed"></vs-radio>
                      <div>
                        <h4 class="mb-2">One fixed amount you define.</h4>
                        <p class="text-gray-600 service-text-base">Best for services like Anand Karaj. Where a person
                          can pick and choose slots between operating hours.</p>
                        <span v-if="payment_option === 'fixed'" class="inline-block bg-green-90 rounded mt-3"
                          style="padding:5px 10px; font-size:17px; color:#021C02;">selected</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="vx-col w-full">
                  <div
                    :class="['option-choice-card border border-gray-300 border-solid rounded cursor-pointer', payment_option === 'flexible' ? 'shadow' : '']"
                    @click="payment_option = 'flexible'">
                    <div class="flex items-start gap-3">
                      <vs-radio v-model="payment_option" vs-name="option_for_payment" vs-value="flexible"></vs-radio>
                      <div>
                        <h4 class="mb-2">Flexible. Let customer choose between multiple options.</h4>
                        <p class="text-gray-600 text-base">Best for services like Punjabi language teaching classes or
                          Pagri teaching classes. Where a person has to choose one defined fixed time only.</p>
                        <span v-if="payment_option === 'flexible'" class="inline-block bg-green-90 rounded mt-3"
                          style="padding:5px 10px; font-size:17px; color:#021C02;">selected</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="bg-gray-400 h-1 opacity-25 w-full my-10"></div>
              <div v-if="payment_option === 'fixed'" class="settings-row">
                <p class="mb-4 service-heading-primary font-bold">Mention Fixed Amount Per Booking</p>
                <div class="vx-row items-start mb-6">
                  <div class="vx-col w-full md:w-2/3">
                    <h5 class="mb-2.5 font-medium" style="font-size:20px;">Your Service Charge</h5>
                    <p class=" mb-5 text-base" style="color:#666666">Total amount the user will see on the Sangat App
                    </p>
                    <div class="service-charge-switch rounded inline-flex items-center gap-4">
                      <vs-switch v-model="isServiceChargePublic">
                        <span slot="on">On</span>
                        <span slot="off">Off</span>
                      </vs-switch>
                      <h5>Make your service charge visible to all.</h5>
                    </div>
                  </div>
                  <div class="vx-col w-full md:w-1/3 flex flex-col md:items-end mt-4 md:mt-0">
                    <div class="charge-currency-block w-full md:max-w-sm">
                      <p class="charge-currency-label">Currency</p>
                      <currency-select
                        v-model="serviceCurrency"
                        :options="currencyOptions"
                        name="service-currency-fixed"
                      />
                      <div class="flex items-center settings-input w-full mt-3">
                        <span class="currency-amount-prefix">{{ currencySymbol }}</span>
                        <vs-input type="number" min="0" v-model.number="serviceCharge" class="w-full" />
                      </div>
                    </div>
                    <span v-if="step2Errors.serviceCharge"
                      class="text-danger text-sm mt-2 block text-left md:text-right settings-input">{{
                        step2Errors.serviceCharge }}</span>
                  </div>
                </div>
                <vs-divider></vs-divider>

                <div class="vx-row items-center mb-6">
                  <div class="vx-col w-full md:w-2/3">
                    <h5 class="mb-2.5 font-medium" style="font-size:20px;">Sangat Platform Fee</h5>
                    <div class="flex items-center vx-col gap-2">
                      <p class=" text-base mr-2" style="color:#666666">This is the fees that will be deduced per booking
                        ({{ platformFeePercentage }}%)
                      </p>
                      <div class="tooltip-wrapper">
                        <feather-icon icon="InfoIcon"
                          :svgClasses="['w-6', 'h-6', 'fill-[#666666]', 'cursor-pointer']" />
                        <span class="tooltip-text">{{ currencySymbol }}{{ platformFeeAmount || 0 }} ({{ platformFeePercentage }}%) will be
                          deducted from {{ currencySymbol }}{{
                            serviceCharge || 0 }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="vx-col w-full md:w-1/3 flex md:justify-end mt-4 md:mt-0">
                    <div class="px-4 py-3 border rounded settings-input text-right text-base text-black">- {{ currencySymbol }} {{
                      platformFeeAmount }}</div>
                  </div>
                </div>

                <vs-divider></vs-divider>

                <div class="vx-row items-center mb-6">
                  <div class="vx-col w-full md:w-2/3">
                    <h5 class="mb-2.5 font-medium" style="font-size:20px;">You'll receive</h5>
                    <div class="flex items-center vx-col gap-2">
                      <p class=" text-base mr-2" style="color:#666666">This is the amount you'll receive pre tax
                        deductions</p>
                      <div class="tooltip-wrapper">
                        <feather-icon icon="InfoIcon"
                          :svgClasses="['w-6', 'h-6', 'fill-[#666666]', 'cursor-pointer']" />
                        <span class="tooltip-text">{{ currencySymbol }}{{ receivedAmount || 0 }} will be received after deducting {{ currencySymbol }}{{
                          platformFeeAmount || 0 }} ({{ platformFeePercentage }}% platform fee) from {{ currencySymbol }}{{ serviceCharge
                            || 0 }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="vx-col w-full md:w-1/3 flex md:justify-end mt-4 md:mt-0">
                    <div class="flex items-center settings-input w-full md:max-w-sm">
                      <span class="currency-amount-prefix">{{ currencySymbol }}</span>
                      <vs-input type="number" min="0" v-model.number="receivedAmount" class="w-full" />
                    </div>
                  </div>
                </div>

              </div>
              <div v-if="payment_option === 'flexible'">
                <p class="mb-4 service-heading-primary font-bold">Mention Multiple Payment Amount Options</p>

                <div class="charge-currency-block mb-6 max-w-md">
                  <p class="charge-currency-label">Currency</p>
                  <currency-select
                    v-model="serviceCurrency"
                    :options="currencyOptions"
                    name="service-currency-flexible"
                  />
                  <p class="text-sm mt-2" style="color: #666666;">Mobile app shows amounts in this currency.</p>
                </div>

                <div class="mb-6">
                  <div class="mb-2 flex flex-col xl:flex-row items-start gap-5">
                    <div class="shrink-0">
                      <h5 class="font-medium mb-2.5 " style="font-size:20px;">Mention all payment amounts</h5>
                      <p class="text-base" style="color: #666666;">Total amount the user will see on the Sangat App</p>
                    </div>
                    <div class="payment-amounts-list w-full max-w-md">
                      <div v-for="(amount, index) in paymentAmounts" :key="index" class="flex items-center gap-5 mb-4">
                        <div class="flex items-center w-full ">
                          <span class="currency-amount-prefix currency-amount-prefix--compact">{{ currencySymbol }}</span>
                          <vs-input placeholder="Enter Amount" type="number" min="0" v-model="amount.value"
                            class="w-full" style="margin:0px;" />
                        </div>
                        <button type="button" class="remove-interval-btn  text-base flex items-center"
                          @click="removePaymentAmount(index)">
                          <feather-icon icon="XIcon" :svgClasses="['w-4', 'h-4']" class="mr-1 pt-1"
                            style="font-size:18px;" /> Remove
                        </button>
                      </div>

                      <a class="add-interval-link flex items-center gap-2 mt-4" @click="addPaymentAmount">
                        <feather-icon icon="PlusIcon" :svgClasses="['w-4', 'h-4']" />
                        <span style="color:#0B1444;">Add New Amount</span>
                      </a>
                      <span v-if="step2Errors.flexibleAmount" class="text-danger text-sm mt-2 block">{{
                        step2Errors.flexibleAmount }}</span>
                    </div>

                  </div>

                </div>

                <div class="mt-8 mb-6">
                  <div class="service-charge-switch rounded inline-flex items-center gap-4 w-fit p-4 bg-gray-100">
                    <vs-switch v-model="allowCustomAmount">
                      <span slot="on">On</span>
                      <span slot="off">Off</span>
                    </vs-switch>
                    <h5 class="font-medium " style="font-size:18px;">Allow customer to mention their own value</h5>
                  </div>
                </div>

                <div v-if="allowCustomAmount" style="margin-bottom:70px;" class="flex flex-col md:flex-row md:items-center gap-5">
                  <h5 class="font-medium mb-2" style="font-size:20px;">Minimum amount customer must enter <br /> while
                    entering custom amount.</h5>
                  <div>
                    <div class="flex items-center settings-input">
                      <span class="currency-amount-prefix currency-amount-prefix--compact">{{ currencySymbol }}</span>
                      <vs-input placeholder="Enter Amount" type="number" min="0" v-model.number="minCustomAmount"
                        class="w-full" style="margin:0px;" />
                    </div>
                    <span v-if="step2Errors.customAmount" class="text-danger text-sm mt-1 block">{{
                      step2Errors.customAmount }}</span>
                  </div>
                </div>
              </div>


            </div>

            <div class="bg-gray-400 h-1 opacity-25 w-full my-10"></div>

            <!-- Step 3 Content: Payment & Policies -->
            <div class="mb-8">

              <p class="mb-4 text-2xl font-bold">When You Want To Collect Payments From Your Customer?</p>
              <div class="vx-col option-cards-grid mb-6">
                <div class="vx-col w-full">
                  <div
                    :class="['option-choice-card option-choice-card--tall border border-solid border-gray-300 rounded cursor-pointer', paymentCollection === 'after_approval' ? 'shadow' : '']"
                    @click="paymentCollection = 'after_approval'">
                    <div class="flex items-start gap-3">
                      <vs-radio v-model="paymentCollection" vs-name="payment_timing"
                        vs-value="after_approval"></vs-radio>
                      <div>
                        <h4 class="mb-2">After Gurudwara Collects And Validates Customer Information</h4>
                        <p class="text-gray-600 service-text-base">Best for services like Anand Karaj. Where gurudwara
                          first needs to validate all documents and then grant approval for confirming booking with
                          payment.</p>
                        <span v-if="paymentCollection === 'after_approval'"
                          class="inline-block bg-green-90 rounded mt-3"
                          style="padding:5px 10px; font-size:17px; color:#021C02;">selected</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="vx-col w-full">
                  <div
                    :class="['option-choice-card option-choice-card--tall border border-solid border-gray-300 rounded cursor-pointer', paymentCollection === 'during_booking' ? 'shadow' : '']"
                    @click="paymentCollection = 'during_booking'">
                    <div class="flex items-start gap-3">
                      <vs-radio v-model="paymentCollection" vs-name="payment_timing"
                        vs-value="during_booking"></vs-radio>
                      <div>
                        <h4 class="mb-2">In Advance During Booking</h4>
                        <p class="text-gray-600 service-text-base">Best for services like Punjabi language teaching
                          classes or Pagri teaching classes.</p>
                        <span v-if="paymentCollection === 'during_booking'"
                          class="inline-block bg-green-90 rounded mt-3"
                          style="padding:5px 10px; font-size:17px; color:#021C02;">selected</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="bg-gray-400 h-1 opacity-25 w-full my-10"></div>
              <p class="mb-4 service-heading-primary font-bold">Write Your Rules And Policies For This Service</p>

              <div class="vx-row mt-5">
                <div class="vx-col md:w-1/1 w-full mb-4" v-for="(info, idx) in infoSections" :key="idx">
                  <div>
                    <p class=" text-md font-bold">{{ info.title }}</p>
                    <vs-textarea v-model="info.value" :placeholder="info.placeholder" height="150px" />
                    <span v-if="getRuleError(info.title)" class="text-danger text-sm mt-2 block">{{
                      getRuleError(info.title) }}</span>
                  </div>
                </div>
              </div>
              <div class="bg-gray-400 h-1 opacity-25 w-full my-10"></div>
              <document-collection-section
                :enabled.sync="collectDocumentsEnabled"
                :documents="documents"
                title="Do You Want To Collect Any Documents From Your Customer?"
                boxed-toggle
              />
            </div>

          </form>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import Select2 from '@/components/custom/form-elements/Select2.vue'
import CurrencySelect from '@/components/custom/form-elements/CurrencySelect.vue'
import DocumentCollectionSection from '@/components/custom/DocumentCollectionSection.vue'
import { calcChargeFromNetReceive, calcNetReceive, calcPlatformFeeAmount } from '@/utils/platformFee'
import { getCurrencySymbol } from '@/utils/currency'

// For custom error message
import { Validator } from 'vee-validate'
const dict = {
  custom: {
    first_name: {
      required: 'First name is required',
      alpha: 'First name may only contain alphabetic characters'
    },
    last_name: {
      required: 'Last name is required',
      alpha: 'Last name may only contain alphabetic characters'
    },
    email: {
      required: 'Email is required',
      email: 'Please enter valid email'
    },
    job_title: {
      required: 'Job title name is required',
      alpha: 'Job title may only contain alphabetic characters'
    },
    proposal_title: {
      required: 'Proposal title name is required',
      alpha: 'Proposal title may only contain alphabetic characters'
    },
    event_name: {
      required: 'Event name is required',
      alpha: 'Event name may only contain alphabetic characters'
    }
  }
}

// register custom messages
Validator.localize('en', dict)

export default {
  /** Component Name */
  name: 'AddUpdateService',

  /** Props */
  props: {
    selectedRecordData: {
      type: Object,
      default: null
    },
    showModal: {
      type: Boolean,
      default: false
    },
  },


  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      city: 'new-york',
      proposalTitle: '',
      jobTitle: '',
      textarea: '',
      eventName: '',
      is_active: true,
      eventLocation: 'san-francisco',
      status: 'Draft',
      cityOptions: [
        { text: 'New York', value: 'new-york' },
        { text: 'Chicago', value: 'chicago' },
        { text: 'San Francisco', value: 'san-francisco' },
        { text: 'Boston', value: 'boston' }
      ],
      statusOptions: [
        { text: 'Plannning', value: 'plannning' },
        { text: 'In Progress', value: 'in progress' },
        { text: 'Finished', value: 'finished' }
      ],
      LocationOptions: [
        { text: 'New York', value: 'new-york' },
        { text: 'Chicago', value: 'chicago' },
        { text: 'San Francisco', value: 'san-francisco' },
        { text: 'Boston', value: 'boston' }
      ],
      // placeholder selections for API fields
      selectedServiceId: null,
      selectedGurudwaraId: null,
      requiresBookingDate: true,
      requiresTimeSlot: true,
      serviceLocationOptions: [
        { label: 'Inside Gurudwara', value: 'Inside' },
        { label: 'Outside Gurudwara', value: 'Outside' }
      ],
      serviceLocationSelection: ['Inside', 'Outside'],
      isEditMode: false,
      existingServiceData: null,
      serviceList: [],
      servicesLoading: false,
      step1Errors: { service: '', description: '', months: '', locations: '', serviceImage: '' },
      serviceImage: null,
      serviceImagePreview: null,
      step2Errors: { serviceCharge: '', flexibleAmount: '', customAmount: '' },
      step3Errors: { payment_policy: '', rules_and_regulations: '', safety_information: '', cancellation_policy: '', refund_policy: '' },
      infoSections: [
        {
          title: 'Payment Terms *',
          placeholder: 'Write in detail about your service fees and terms',
          value: ''
        },
        {
          title: 'Rules And Regulations *',
          placeholder: 'Write your gurudwara rules and regulations here',
          value: ''
        },
        {
          title: 'Safety Information *',
          placeholder: 'Write your gurudwara safety information here',
          value: ''
        },
        {
          title: 'Cancellation Policy *',
          placeholder: 'Write your gurudwara service cancellation policy here',
          value: ''
        },
        {
          title: 'Refund Policy *',
          placeholder: 'Write your gurudwara service refund policy here',
          value: ''
        }
      ],
      months: [
        { name: 'January', value: false },
        { name: 'February', value: false },
        { name: 'March', value: false },
        { name: 'April', value: false },
        { name: 'May', value: false },
        { name: 'June', value: false },
        { name: 'July', value: false },
        { name: 'August', value: false },
        { name: 'September', value: false },
        { name: 'October', value: false },
        { name: 'November', value: false },
        { name: 'December', value: false }
      ],
      days: [
        { name: 'sunday', label: 'Sunday', enabled: false, intervals: [] },
        { name: 'monday', label: 'Monday', enabled: false, intervals: [] },
        { name: 'tuesday', label: 'Tuesday', enabled: false, intervals: [] },
        { name: 'wednesday', label: 'Wednesday', enabled: false, intervals: [] },
        { name: 'thursday', label: 'Thursday', enabled: false, intervals: [] },
        { name: 'friday', label: 'Friday', enabled: false, intervals: [] },
        { name: 'saturday', label: 'Saturday', enabled: false, intervals: [] }
      ],
      sunday: true,
      time: null,
      configdateTimePicker: {
        enableTime: true,
        enableSeconds: true,
        noCalendar: true
      },
      maxBookingsPerSlot: 1,
      intervalMode: 'flexible',
      payment_option: 'fixed',
      paymentCollection: 'before_booking',
      collectDocumentsEnabled: false,
      requiredDocuments: [],
      documents: [
        { name: 'Aadhaar Card', mandatory: true, hint: 'Upload both front and back side together' },
        { name: 'PAN Card', mandatory: false, hint: 'Upload both front and back side together' },
        { name: 'Passport', mandatory: true, hint: 'Upload both front and back side together' }
      ],
      isServiceChargePublic: false,
      serviceCurrency: 'INR',
      currencyOptions: [],
      serviceCharge: 0,
      platformFeeAmount: 0, // Set from master service selection
      platformFeePercentage: 0, // For display in tooltips if needed
      isEditing: !!this.selectedRecordData,
      existingServiceImage: null, // Store existing service image URL
      receivedAmount: 0, // For reverse calculation
      isCalculatingFromReceived: false, // Flag to prevent circular updates
      dayErrors: [],
      submittingService: false,
      is24hOpen: false,
      paymentAmounts: [{ value: '' }],
      allowCustomAmount: false,
      minCustomAmount: ''
    }
  },

  created() {

    // Capture gurudwara id from route: /gurudwara/:id/edit
    if (this.$route && this.$route.params && this.$route.params.id) {
      this.selectedGurudwaraId = this.$route.params.id
    }

    // Check if we're editing an existing service
    if (this.$route && this.$route.query && this.$route.query.serviceId) {
      this.isEditMode = true
      this.selectedServiceId = this.$route.query.serviceId
    }

    const selectedRecord = this.selectedRecordData
    if (selectedRecord) {
      this.isEditMode = true
      if (selectedRecord.service && selectedRecord.service._id) {
        this.selectedServiceId = selectedRecord.service._id
      }
      if (selectedRecord.gurudwara && selectedRecord.gurudwara._id) {
        this.selectedGurudwaraId = selectedRecord.gurudwara._id
      } else if (selectedRecord.gurudwara) {
        this.selectedGurudwaraId = selectedRecord.gurudwara
      }
    }

    // Load services after gurudwara/edit context is known so already-created
    // services can be excluded from the dropdown.
    this.loadServicesList();

    if (this.$route && this.$route.query && this.$route.query.serviceId) {
      this.loadExistingService()
    }

    // Prefill form with data if selectedRecordData exists
    const d = this.selectedRecordData
    if (!d) return
    this.isEditMode = true
    this.is24hOpen = d.is24hOpen || false

    // --- Service ID (CRITICAL - needed for dropdown) ---
    if (d.service && d.service._id) {
      this.selectedServiceId = d.service._id;
    }

    // --- Service Location (CRITICAL - used in radio buttons) ---
    if (Array.isArray(d.service_location) && d.service_location.length) {
      this.serviceLocationSelection = this.normalizeServiceLocations(d.service_location);
    } else if (d.service_location) {
      this.serviceLocationSelection = this.normalizeServiceLocations([d.service_location]);
    }

    // --- Gurudwara ID ---
    if (d.gurudwara && d.gurudwara._id) {
      this.selectedGurudwaraId = d.gurudwara._id;
    }

    this.is_active = d.is_active || true;

    if (d.requires_booking_date !== undefined && d.requires_booking_date !== null) {
      this.requiresBookingDate = !!d.requires_booking_date;
    }
    if (d.requires_time_slot !== undefined && d.requires_time_slot !== null) {
      this.requiresTimeSlot = !!d.requires_time_slot;
    } else if (!this.requiresBookingDate) {
      this.requiresTimeSlot = false;
    }

    // --- Basic Details ---
    this.textarea = d.service_description || '';
    this.status = d.status || 'Draft';
    this.eventLocation = Array.isArray(this.serviceLocationSelection) && this.serviceLocationSelection.length
      ? this.serviceLocationSelection[0].toLowerCase()
      : 'inside';

    // Note: eventName is not used in the template, kept for reference
    if (d.service && d.service.service_name) {
      this.eventName = d.service.service_name;
    }

    // --- Months ---
    if (d.opening_month) {
      const monthMap = {
        January: 'jan',
        February: 'feb',
        March: 'mar',
        April: 'apr',
        May: 'may',
        June: 'jun',
        July: 'jul',
        August: 'aug',
        September: 'sep',
        October: 'oct',
        November: 'nov',
        December: 'dec'
      };

      this.months = this.months.map(m => {
        const shortKey = monthMap[m.name];
        return {
          ...m,
          value: d.opening_month[shortKey] || false
        };
      });
    }

    if (d.operating_days) {
      this.days = this.days.map(day => {
        const dayData = d.operating_days[day.name] || {};
        return {
          ...day,
          enabled: !!dayData.available,
          intervals: Array.isArray(dayData.hours)
            ? [...dayData.hours]
            : []
        };
      });
    }

    // --- Payment & Charges ---
    this.paymentCollection = d.collect_payments === 'AfterApproval' ? 'after_approval' : 'during_booking'
    this.maxBookingsPerSlot = d.maximum_booking_per_slot || 1;
    this.intervalMode = d.time_slot === 'Flexible' ? 'flexible' : 'fixed';
    this.payment_option = d.payment_option === 'Flexible' ? 'flexible' : 'fixed';
    this.serviceCharge = d.total_service_amount || 0;
    this.serviceCurrency = d.currency || 'INR';
    this.isServiceChargePublic = d.is_service_charge_public || false;

    // Populate custom amounts
    if (Array.isArray(d.payment_amounts) && d.payment_amounts.length > 0) {
      this.paymentAmounts = d.payment_amounts.map(val => {
        // Handle if value is object or primitive
        return { value: (val && typeof val === 'object' && val.value) ? val.value : val }
      })
    } else {
      this.paymentAmounts = [{ value: '' }]
    }
    this.allowCustomAmount = d.allow_custom_amount === true || d.allow_custom_amount === 'true'
    this.minCustomAmount = d.min_custom_amount || ''
    // Use platform fee amount from data
    this.platformFeeAmount = d.platform_fee_amount || 0;

    // Calculate received amount (never negative even if a bad percentage was saved)
    const charge = Number(this.serviceCharge) || 0
    const platformFee = Number(this.platformFeeAmount) || 0
    this.receivedAmount = Math.max(0, charge - platformFee).toFixed(2)

    // Load existing service image if available
    if (d.service_image) {
      this.existingServiceImage = d.service_image
      this.serviceImagePreview = d.service_image
    }

    // --- Info Sections (Rules & Policies) ---
    if (d.rules_and_policies) {
      const policies = d.rules_and_policies;

      this.infoSections = this.infoSections.map(section => {
        const title = section.title.toLowerCase();
        let key = '';

        if (title.includes('payment')) key = 'payment_policy';
        else if (title.includes('rules')) key = 'rules_and_regulations';
        else if (title.includes('safety')) key = 'safety_information';
        else if (title.includes('cancellation')) key = 'cancellation_policy';
        else if (title.includes('refund')) key = 'refund_policy';

        return {
          ...section,
          value: key ? policies[key] || '' : section.value
        };
      });
    }

    // --- Document Collection ---
    this.collectDocumentsEnabled = !!d.is_documentCollect;
    this.requiredDocuments = Array.isArray(d.collect_documents)
      ? [...d.collect_documents]
      : [];

    // Load documents from API if available, otherwise keep defaults
    if (Array.isArray(d.collect_documents) && d.collect_documents.length > 0) {
      this.documents = d.collect_documents.map((doc) => ({
        _editing: false,
        _dirty: false,
        _backup: null,
        name: doc.document_name || doc.name,
        mandatory: (doc.is_required !== undefined && doc.is_required !== null)
          ? doc.is_required
          : doc.mandatory,
        hint: doc.description || doc.hint
      }));
    } else {
      // Add reactive properties to default documents
      this.documents = this.documents.map((doc) => ({
        _editing: false,
        _dirty: false,
        _backup: null,
        name: doc.name,
        mandatory: doc.mandatory,
        hint: doc.hint
      }));
    }
  },

  computed: {
    wizardTitle() {
      const selected = this.serviceList.find((service) => service.value === this.selectedServiceId)
      if (selected && selected.label) {
        return selected.label
      }

      if (this.existingServiceData && this.existingServiceData.service && this.existingServiceData.service.service_name) {
        return this.existingServiceData.service.service_name
      }

      return 'Service'
    },
    currencySymbol() {
      return getCurrencySymbol(this.serviceCurrency)
    },
    netReceive() {
      // If receivedAmount is set (user entered it), use that
      if (this.receivedAmount > 0) {
        return Number(this.receivedAmount).toFixed(2)
      }
      // Otherwise calculate from service charge
      const charge = Number(this.serviceCharge) || 0
      const fee = Number(this.platformFeeAmount)
      return Math.max(0, charge - fee).toFixed(2)
    },
    selectedMonthsCount() {
      return this.months.filter((m) => m.value).length
    },
    allMonthsSelected() {
      return this.months.length > 0 && this.months.every((month) => !!month.value)
    },
    hasAnyDayErrors() {
      return this.dayErrors.some((error) => !!error)
    },
    isActive: {
      get() {
        return this.showModal
      },
      set(val) {
        this.$emit('update:showModal', val)
      }
    },
    isModelActive: {
      get() {
        return this.showModal
      },
      set(val) {
        this.$emit('update:showModal', val)
      }
    }
  },

  watch: {
    // Recompute per-day errors whenever the schedule changes
    days: {
      handler() {
        const newErrors = []
        for (let d = 0; d < this.days.length; d++) {
          newErrors[d] = this.getDayError(this.days[d])
        }
        this.dayErrors = newErrors
      },
      deep: true
    },
    // Clear Step 1 service error when user selects a service
    selectedServiceId(newVal) {
      if (newVal) {
        this.step1Errors.service = ''
        const selected = this.serviceList.find((service) => service.value === newVal)
        if (selected) {
          this.platformFeePercentage = selected.collection_platform_fee || 0
          this.calculateFees()
          if (!this.isEditMode) {
            this.requiresBookingDate = selected.requires_booking_date !== false
            this.requiresTimeSlot = selected.requires_time_slot !== false && this.requiresBookingDate
          }
        }
      }
    },
    // Clear Step 1 description error when user types
    textarea(newVal) {
      if (newVal && newVal.trim()) this.step1Errors.description = ''
    },
    // Clear Step 1 months error when any month toggled on
    months: {
      handler() {
        const anyMonthOn = this.months.some((m) => !!m.value)
        if (anyMonthOn) this.step1Errors.months = ''
      },
      deep: true
    },
    serviceLocationSelection: {
      handler(newVal) {
        if (Array.isArray(newVal) && newVal.length) {
          this.step1Errors.locations = ''
        }
      },
      deep: true
    },
    // Clear Step 1 service image error when user uploads an image
    serviceImage(newVal) {
      if (newVal) this.step1Errors.serviceImage = ''
    },
    // Watch for service charge changes to calculate received amount
    serviceCharge(newVal) {
      // Skip if we're calculating from received amount to prevent circular updates
      if (this.isCalculatingFromReceived) {
        return
      }

      const charge = Number(newVal) || 0
      this.calculateFees()

      // Clear errors
      const platformFee = Number(this.platformFeeAmount) || 0
      if (charge > 0 && charge >= platformFee) {
        this.step2Errors.serviceCharge = ''
      }
    },
    // Watch for received amount changes to calculate service charge backwards
    receivedAmount(newVal) {
      if (newVal > 0 && !this.isCalculatingFromReceived) {
        // Set flag to prevent circular update
        this.isCalculatingFromReceived = true

        const received = Number(newVal) || 0
        this.serviceCharge = calcChargeFromNetReceive(received, this.platformFeePercentage)
        this.platformFeeAmount = calcPlatformFeeAmount(this.serviceCharge, this.platformFeePercentage)

        // Reset flag after calculation
        this.$nextTick(() => {
          this.isCalculatingFromReceived = false
        })
      } else if (newVal === 0 || newVal === null || newVal === '') {
        // Reset if cleared
        this.receivedAmount = 0
        if (!this.isCalculatingFromReceived) {
          this.serviceCharge = 0
          this.platformFeeAmount = 0
        }
      }
    },
    paymentAmounts: {
      handler() {
        if (this.paymentAmounts.some(a => a.value && Number(a.value) > 0)) {
          this.step2Errors.flexibleAmount = ''
        }
      },
      deep: true
    },
    minCustomAmount(newVal) {
      if (newVal) this.step2Errors.customAmount = ''
    },
    // Clear Step 3 rules/policies errors as user types
    infoSections: {
      handler() {
        this.infoSections.forEach((section) => {
          const key = this.getRulesKey(section.title)
          if (section.value && section.value.trim()) {
            if (this.step3Errors[key] !== undefined) this.step3Errors[key] = ''
          }
        })
      },
      deep: true
    },
    submittingService(val) {
      if (val) {
        this.$nextTick(() => {
          const publishBtn = document.getElementById('publish-btn')
          const draftBtn = document.getElementById('save-draft-btn')

          if (publishBtn && !publishBtn.classList.contains('vs-con-loading__container')) {
            publishBtn.classList.add('vs-con-loading__container')
            this.$vs.loading({ container: '#publish-btn', scale: 0.45 })
          }
          if (draftBtn && !draftBtn.classList.contains('vs-con-loading__container')) {
            draftBtn.classList.add('vs-con-loading__container')
            this.$vs.loading({ container: '#save-draft-btn', scale: 0.45 })
          }
        })
      } else {
        this.$nextTick(() => {
          try {
            this.$vs.loading.close('#publish-btn > .con-vs-loading')
            this.$vs.loading.close('#save-draft-btn > .con-vs-loading')
          } catch (e) {
            // no-op
          }
          const publishBtn = document.getElementById('publish-btn')
          const draftBtn = document.getElementById('save-draft-btn')
          if (publishBtn) {
            publishBtn.classList.remove('vs-con-loading__container')
          }
          if (draftBtn) {
            draftBtn.classList.remove('vs-con-loading__container')
          }
        })
      }
    },
    payment_option(newVal) {
      this.step2Errors.serviceCharge = ''
      this.step2Errors.flexibleAmount = ''
      this.step2Errors.customAmount = ''
      if (newVal === 'fixed') {
        this.paymentAmounts = [{ value: '' }]
        this.minCustomAmount = ''
        // this.allowCustomAmount = false
      } else if (newVal === 'flexible') {
        this.serviceCharge = 0
      }
    },
    allowCustomAmount(val) {
      if (!val) {
        this.step2Errors.customAmount = ''
        this.minCustomAmount = ''
      }
    }
  },
  methods: {
    onRequiresBookingDateChange(value) {
      if (!value) {
        this.requiresTimeSlot = false
      }
    },
    calculateFees() {
      const charge = Number(this.serviceCharge) || 0
      this.platformFeeAmount = calcPlatformFeeAmount(charge, this.platformFeePercentage)
      this.receivedAmount = calcNetReceive(charge, this.platformFeePercentage).toFixed(2)
    },
    isServiceLocationSelected(value) {
      if (!value) return false
      if (!Array.isArray(this.serviceLocationSelection)) return false
      const normalizedValue = this.normalizeServiceLocationValue(value)
      return this.serviceLocationSelection.includes(normalizedValue)
    },
    toggleServiceLocation(value) {
      const normalizedValue = this.normalizeServiceLocationValue(value)
      if (!normalizedValue) return
      const current = Array.isArray(this.serviceLocationSelection) ? [...this.serviceLocationSelection] : []
      const existingIndex = current.indexOf(normalizedValue)
      if (existingIndex > -1) {
        current.splice(existingIndex, 1)
      } else {
        current.push(normalizedValue)
      }
      this.serviceLocationSelection = current
    },
    normalizeServiceLocationValue(value) {
      if (value === null || value === undefined) return null
      const normalized = String(value).toLowerCase()
      if (normalized.includes('inside')) return 'Inside'
      if (normalized.includes('outside')) return 'Outside'
      return String(value)
    },
    normalizeServiceLocations(values) {
      if (!Array.isArray(values)) return ['Inside', 'Outside']
      const normalized = values
        .map((value) => this.normalizeServiceLocationValue(value))
        .filter(Boolean)
      return normalized.length ? Array.from(new Set(normalized)) : ['Inside', 'Outside']
    },
    handleImageUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg']
      if (!validTypes.includes(file.type)) {
        this.step1Errors.serviceImage = 'Only PNG and JPG format is supported'
        this.$vs.notify({
          title: 'Invalid File Type',
          text: 'Only PNG and JPG format is supported',
          color: 'danger',
          position: 'top-center'
        })
        event.target.value = ''
        return
      }

      // Validate file size (1 MB = 1048576 bytes)
      const maxSize = 1048576
      if (file.size > maxSize) {
        this.step1Errors.serviceImage = 'File size should be less than 1 MB'
        this.$vs.notify({
          title: 'File Too Large',
          text: 'File size should be less than 1 MB',
          color: 'danger',
          position: 'top-center'
        })
        event.target.value = ''
        return
      }

      // Clear any previous errors
      this.step1Errors.serviceImage = ''

      // Store the file
      this.serviceImage = file

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        this.serviceImagePreview = e.target.result
      }
      reader.readAsDataURL(file)
    },
    removeImage() {
      this.serviceImage = null
      this.serviceImagePreview = null
      this.existingServiceImage = null // Also clear existing image when removed
      this.step1Errors.serviceImage = ''
      if (this.$refs.serviceImageInput) {
        this.$refs.serviceImageInput.value = ''
      }
    },
    handleBackClick() {
      this.isModelActive = false
    },
    async handleSaveDraft() {
      // Save as draft - skip validation, set status to Draft
      this.status = 'Draft'
      this.is_active = true
      // Clear any existing errors for draft save
      this.step1Errors = { service: '', description: '', months: '', locations: '', serviceImage: '' }
      this.step2Errors = { serviceCharge: '', flexibleAmount: '', customAmount: '' }
      this.step3Errors = { payment_policy: '', rules_and_regulations: '', safety_information: '', cancellation_policy: '', refund_policy: '' }
      await this.submitService(false) // false = skip full validation
    },
    async handlePublish() {
      // Publish - full validation required, set status to Publish/Active
      const isValid = await this.validateAll()
      if (isValid) {
        this.status = 'Publish'
        this.is_active = true
        await this.submitService(true) // true = validation already done
      }
    },
    async validateAll() {
      // Reset all errors
      this.step1Errors = { service: '', description: '', months: '', locations: '', serviceImage: '' }
      this.step2Errors = { serviceCharge: '', flexibleAmount: '', customAmount: '' }
      this.step3Errors = { payment_policy: '', rules_and_regulations: '', safety_information: '', cancellation_policy: '', refund_policy: '' }

      // Validate Step 1 fields
      if (!this.selectedServiceId) {
        this.step1Errors.service = 'Service is required'
      }
      if (!this.textarea || !this.textarea.trim()) {
        this.step1Errors.description = 'Service description is required'
      }
      const anyMonthOn = this.months.some((m) => !!m.value)
      if (!anyMonthOn) {
        this.step1Errors.months = 'Select at least one operating month'
      }
      if (!Array.isArray(this.serviceLocationSelection) || !this.serviceLocationSelection.length) {
        this.step1Errors.locations = 'Select at least one service location'
      }
      // Allow existing image when editing, otherwise require new image
      if (!this.serviceImage && !this.existingServiceImage) {
        this.step1Errors.serviceImage = 'Service image is required'
      }

      // Validate Step 2 fields
      const newErrors = []
      for (let d = 0; d < this.days.length; d++) {
        newErrors[d] = this.getDayError(this.days[d])
      }
      this.dayErrors = newErrors

      if (this.payment_option === 'fixed') {
        const serviceChargeValue = Number(this.serviceCharge)
        const platformFeeValue = Number(this.platformFeeAmount) || 0
        if (!serviceChargeValue || serviceChargeValue <= 0) {
          this.step2Errors.serviceCharge = 'Service fees per booking is required'
        } else if (serviceChargeValue < platformFeeValue) {
          this.step2Errors.serviceCharge = `Service fees per booking must be at least the Sangat platform fee (${this.currencySymbol}${platformFeeValue})`
        }

        // Ensure received amount is calculated correctly and never negative
        if (serviceChargeValue > 0) {
          this.receivedAmount = calcNetReceive(serviceChargeValue, this.platformFeePercentage).toFixed(2)
        }
      } else {
        // Flexible validation
        const validAmounts = this.buildPaymentAmountsPayload()
        if (validAmounts.length === 0) {
          this.step2Errors.flexibleAmount = 'At least one amount is required'
        }

        if (this.allowCustomAmount && (!this.minCustomAmount || Number(this.minCustomAmount) <= 0)) {
          this.step2Errors.customAmount = 'Min amount is required'
        }
      }


      // Validate Step 3 fields
      const getVal = (title) => {
        const sec = this.infoSections.find((s) => s.title === title)
        return sec ? (sec.value || '').trim() : ''
      }
      if (!getVal('Payment Terms *')) this.step3Errors.payment_policy = 'Payment Terms is required'
      if (!getVal('Rules And Regulations *')) this.step3Errors.rules_and_regulations = 'Rules And Regulations is required'
      if (!getVal('Safety Information *')) this.step3Errors.safety_information = 'Safety Information is required'
      if (!getVal('Cancellation Policy *')) this.step3Errors.cancellation_policy = 'Cancellation Policy is required'
      if (!getVal('Refund Policy *')) this.step3Errors.refund_policy = 'Refund Policy is required'

      // Check if there are any errors
      const hasStep1Errors = Object.values(this.step1Errors).some((v) => !!v)
      const hasStep2Errors = Object.values(this.step2Errors).some((v) => !!v) || this.dayErrors.some((e) => !!e)
      const hasStep3Errors = Object.values(this.step3Errors).some((v) => !!v)

      if (hasStep1Errors || hasStep2Errors || hasStep3Errors) {
        // Scroll to first error
        this.$nextTick(() => {
          const firstError = document.querySelector('.text-danger')
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
        this.$vs.notify({
          title: 'Validation Error',
          text: 'Please fix all errors before submitting',
          color: 'danger',
          position: 'top-center'
        })
        return false
      }

      return true
    },
    getInfoSectionValue(title) {
      const sec = this.infoSections.find((s) => s.title === title)
      return sec ? sec.value : ''
    },
    getRuleError(title) {
      const map = {
        'Payment Terms *': 'payment_policy',
        'Rules And Regulations *': 'rules_and_regulations',
        'Safety Information *': 'safety_information',
        'Cancellation Policy *': 'cancellation_policy',
        'Refund Policy *': 'refund_policy'
      }
      const key = map[title]
      return key ? this.step3Errors[key] : ''
    },
    // Commented out - using hardcoded 20% platform fee instead
    // async loadPlatformFee() {
    //   try {
    //     const res = await this.$http.get('admin/platform-fee/details')
    //     const data = (res && res.data && res.data.data) || {}
    //     const value =
    //       data.platForm_fee != null ? data.platForm_fee : null
    //     if (value != null) this.platformFeeAmount = Number(value)
    //   } catch (error) {
    //     this.$vs.notify({ title: 'Error', text: 'Failed to load platform fee', color: 'danger', position: 'top-center', time: 2500 })
    //   }
    // },
    // Validate and return an inline error message for a specific day (or empty string if valid)
    getDayError(day) {
      if (!day.enabled) return ''
      if (!Array.isArray(day.intervals) || day.intervals.length === 0) {
        return 'Please add at least one time interval'
      }

      const normalized = []
      for (let i = 0; i < day.intervals.length; i++) {
        const interval = day.intervals[i]
        const startSec = this.timeToSeconds(interval.start)
        const endSec = this.timeToSeconds(interval.end)

        if ((interval.start && !interval.end) || (!interval.start && interval.end)) {
          return `Interval ${i + 1}: both start and end times are required`
        }
        if (startSec == null || endSec == null) {
          return `Interval ${i + 1}: please select start and end times`
        }
        if (startSec >= endSec) {
          return `Interval ${i + 1}: start time must be before end time`
        }
        normalized.push([startSec, endSec])
      }

      const sorted = normalized.slice().sort((a, b) => a[0] - b[0])
      for (let i = 1; i < sorted.length; i++) {
        if (this.intervalsOverlap(sorted[i - 1], sorted[i])) {
          return 'Overlapping time intervals detected'
        }
      }
      return ''
    },
    // Convert a flatpickr time value (Date or string) to seconds since midnight
    timeToSeconds(value) {
      if (!value) return null
      if (value instanceof Date) {
        return value.getHours() * 3600 + value.getMinutes() * 60 + value.getSeconds()
      }
      // expect formats like HH:mm or HH:mm:ss
      if (typeof value === 'string') {
        const parts = value.split(':').map((p) => parseInt(p, 10))
        const [h = 0, m = 0, s = 0] = parts
        return h * 3600 + m * 60 + s
      }
      return null
    },
    // Check if two [start,end] intervals (in seconds) overlap
    intervalsOverlap(a, b) {
      const [aStart, aEnd] = a
      const [bStart, bEnd] = b
      // overlap if max(start) < min(end)
      return Math.max(aStart, bStart) < Math.min(aEnd, bEnd)
    },
    handleDayToggle(dayIndex, value) {
      const day = this.days[dayIndex]
      if (!day) return
      const enabled = typeof value === 'boolean' ? value : day.enabled
      day.enabled = enabled
      if (enabled) {
        if (!Array.isArray(day.intervals)) {
          this.$set(day, 'intervals', [])
        }
        if (day.intervals.length === 0) {
          day.intervals.push({ start: '', end: '' })
        }
      }
    },
    addInterval(dayIndex) {
      const day = this.days[dayIndex]
      if (!day || !Array.isArray(day.intervals)) {
        this.$set(this.days[dayIndex], 'intervals', [])
      }
      this.days[dayIndex].intervals.push({ start: '', end: '' })
    },
    removeInterval(dayIndex, intervalIndex) {
      const day = this.days[dayIndex]
      if (!day || !Array.isArray(day.intervals)) return
      day.intervals.splice(intervalIndex, 1)
    },
    async copyToAllDays(sourceIndex) {
      // Prevent copying if the source day has errors
      if (this.dayErrors[sourceIndex]) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please fix errors on this day before copying to all days',
          color: 'danger',
          position: 'top-center'
        })
        return
      }

      await new Promise(resolve => setTimeout(resolve, 200))
      const sourceIntervals = this.days[sourceIndex].intervals.map((i) => ({
        start: i.start,
        end: i.end
      }))

      this.days.forEach((day, index) => {
        if (index !== sourceIndex) {
          day.enabled = true
          day.intervals = sourceIntervals.map((i) => ({
            start: i.start,
            end: i.end
          }))
        }
      })
    },
    handle24hToggle() {
      if (this.is24hOpen) {
        this.days.forEach((day, index) => {
          day.enabled = true
          day.intervals = [{ start: '00:00', end: '23:59' }]
        })
      }
    },
    setAllMonths(value) {
      const enabled = !!value
      this.months.forEach((month) => {
        month.value = enabled
      })
    },
    addPaymentAmount() {
      this.paymentAmounts.push({ value: '' })
    },
    removePaymentAmount(index) {
      if (this.paymentAmounts.length > 0) {
        this.paymentAmounts.splice(index, 1)
      }
    },
    buildPaymentAmountsPayload() {
      if (!Array.isArray(this.paymentAmounts)) {
        return []
      }
      return this.paymentAmounts
        .map((row) => {
          const raw = row && row.value !== undefined && row.value !== null ? row.value : row
          const parsed = parseFloat(String(raw).replace(/,/g, '').trim())
          return parsed
        })
        .filter((amount) => !Number.isNaN(amount) && amount > 0)
    },
    // Build payload and submit create-or-edit service
    async submitService(validationDone = false) {
      const operating_month = {}
      this.months.forEach((m) => {
        const key = this.getMonthKey(m.name)
        operating_month[key] = !!m.value
      })

      // Build operating_days from Step 2
      const operating_days = {}
      this.days.forEach((d) => {
        if (d.enabled) {
          operating_days[d.name] = {
            available: true,
            hours: d.intervals.map((i) => ({ start: i.start, end: i.end }))
          }
        } else {
          operating_days[d.name] = { available: false }
        }
      })

      // Map interval mode to API expected value
      const time_slot = this.intervalMode === 'flexible' ? 'Flexible' : 'Fixed'
      const payment_option = this.payment_option === 'flexible' ? 'Flexible' : 'Fixed'


      // Map payment collection to API expected value
      const collect_payments = this.paymentCollection === 'after_approval' ? 'AfterApproval' : 'AdvanceBooking'

      // Build rules_and_policies from Step 3 infoSections
      const rules_and_policies = {
        payment_policy: this.getInfoSectionValue('Payment Terms *'),
        rules_and_regulations: this.getInfoSectionValue('Rules And Regulations *'),
        safety_information: this.getInfoSectionValue('Safety Information *'),
        cancellation_policy: this.getInfoSectionValue('Cancellation Policy *'),
        refund_policy: this.getInfoSectionValue('Refund Policy *')
      };

      // Build collect_documents from Step 3 documents array
      const collect_documents = this.collectDocumentsEnabled ? this.documents.map((doc) => ({
        document_name: doc.name,
        is_required: doc.mandatory,
        description: doc.hint
      })) : []

      const payment_amounts = this.buildPaymentAmountsPayload()
      let total_service_amount = Number(this.serviceCharge) || 0
      if (payment_option === 'Flexible' && payment_amounts.length > 0) {
        total_service_amount = Math.max(...payment_amounts)
      }

      const payload = {
        status: this.status,
        service: this.selectedServiceId || '',
        service_location: this.getServiceLocation(),
        service_description: this.textarea || '',
        gurudwara: this.selectedGurudwaraId || '',
        opening_month: operating_month,
        operating_days,
        time_slot,
        payment_option,
        maximum_booking_per_slot: Number(this.maxBookingsPerSlot) || 1,
        total_service_amount,
        currency: this.serviceCurrency || 'INR',
        platform_fee_amount: Number(this.platformFeeAmount) || 0,
        gurudwara_receive_amount: Number(this.netReceive) || 0,
        collect_payments,
        rules_and_policies,
        collect_documents,
        is_active: this.is_active,
        is_documentCollect: this.collectDocumentsEnabled,
        is_service_charge_public: this.isServiceChargePublic,
        is24hOpen: this.is24hOpen,
        payment_amounts,
        allow_custom_amount: this.allowCustomAmount,
        min_custom_amount: Number(this.minCustomAmount) || 0,
        requires_booking_date: !!this.requiresBookingDate,
        requires_time_slot: !!this.requiresBookingDate && !!this.requiresTimeSlot
      }

      const gurudwaraServiceId = (this.selectedRecordData && this.selectedRecordData._id)
        || (this.existingServiceData && this.existingServiceData._id)
      if (this.isEditMode && gurudwaraServiceId) {
        payload.gurudwara_service_id = gurudwaraServiceId
      }

      const url = 'admin/gurudwara-service/create-or-edit'
      this.submittingService = true
      try {
        let response
        const isDraft = this.status === 'Draft'
        const actionText = isDraft ? 'saved as draft' : 'published'


        const formData = new FormData()

        // Append service image if it exists
        if (this.serviceImage) {
          formData.append('service_image', this.serviceImage)
        }

        const appendToFormData = (formData, key, value) => {
          if (value === null || value === undefined) {
            return
          }

          if (Array.isArray(value)) {
            formData.append(key, JSON.stringify(value))
          } else if (typeof value === 'object' && !(value instanceof File) && !(value instanceof Date)) {

            formData.append(key, JSON.stringify(value))
          } else {
            formData.append(key, value)
          }
        }

        // Append all other fields to FormData
        Object.keys(payload).forEach(key => {
          appendToFormData(formData, key, payload[key])
        })

        const requestPayload = formData
        const config = {}

        if (this.isEditMode) {
          // Update existing service
          response = await this.$http.post(url, requestPayload, config)
          this.$vs && this.$vs.notify && this.$vs.notify({
            title: 'Success',
            text: `Service ${actionText} successfully`,
            color: 'success',
            position: 'top-center'
          })
          this.$emit('update-data', true)
          this.isActive = false
        } else {
          // Create new service
          response = await this.$http.post(url, requestPayload, config)
          this.$vs && this.$vs.notify && this.$vs.notify({
            title: 'Success',
            text: `Service ${actionText} successfully`,
            color: 'success',
            position: 'top-center'
          })
          this.$emit('update-data', true)
          this.isActive = false
        }
      } catch (error) {
        const message = (error && error.response && error.response.data && error.response.data.message) || error.message || 'Submission failed'
        this.$vs && this.$vs.notify && this.$vs.notify({ title: 'Error', text: message, color: 'danger', position: 'top-center' })
        throw new Error(message)
      } finally {
        this.submittingService = false
      }
    },
    // Helper to get API month key: convert full month name to lower 3-letter key
    getMonthKey(name) {
      const map = {
        January: 'jan',
        February: 'feb',
        March: 'mar',
        April: 'apr',
        May: 'may',
        June: 'jun',
        July: 'jul',
        August: 'aug',
        September: 'sep',
        October: 'oct',
        November: 'nov',
        December: 'dec'
      }
      return map[name] || name
    },
    // Resolve service location selections (Inside/Outside Gurudwara) and normalise to array
    getServiceLocation() {
      const selection = Array.isArray(this.serviceLocationSelection)
        ? this.serviceLocationSelection.filter(Boolean)
        : [this.serviceLocationSelection].filter(Boolean)
      const normalized = selection
        .map((value) => this.normalizeServiceLocationValue(value))
        .filter(Boolean)
      if (!normalized.length) {
        return ['Inside', 'Outside']
      }
      return Array.from(new Set(normalized))
    },
    // Load existing service data for editing
    async loadExistingService() {
      if (!this.selectedServiceId) return

      try {
        const response = await this.$http.get(`admin/gurudwara-service/${this.selectedServiceId}`)
        this.existingServiceData = response.data.data

        // Populate form with existing data
        this.populateFormWithExistingData()
      } catch (error) {
        console.error('Error loading service data:', error)
        this.$vs && this.$vs.notify && this.$vs.notify({
          title: 'Error',
          text: 'Failed to load service data',
          color: 'danger',
          position: 'top-center'
        })
      }
    },
    // Populate form fields with existing service data
    populateFormWithExistingData() {
      if (!this.existingServiceData) return

      const data = this.existingServiceData

      // Step 1 data
      this.eventName = data.service_name || ''
      this.textarea = data.service_description || ''
      if (Array.isArray(data.service_location) && data.service_location.length) {
        this.serviceLocationSelection = this.normalizeServiceLocations(data.service_location)
      } else if (data.service_location) {
        this.serviceLocationSelection = this.normalizeServiceLocations([data.service_location])
      } else {
        this.serviceLocationSelection = ['Inside', 'Outside']
      }

      // Operating months
      if (data.opening_month) {
        this.months.forEach(month => {
          const key = this.getMonthKey(month.name)
          month.value = !!data.opening_month[key]
        })
      }

      // Step 2 data
      if (data.operating_days) {
        this.days.forEach(day => {
          const dayData = data.operating_days[day.name]
          if (dayData) {
            day.enabled = dayData.available
            if (dayData.hours) {
              day.intervals = dayData.hours.map(hour => ({
                start: hour.start,
                end: hour.end
              }))
            }
          }
        })
      }

      this.intervalMode = data.time_slot === 'Flexible' ? 'flexible' : 'fixed'
      this.payment_option = data.payment_option === 'Flexible' ? 'flexible' : 'fixed'
      this.maxBookingsPerSlot = data.maximum_booking_per_slot || 1
      this.serviceCharge = data.total_service_amount || 20
      this.serviceCurrency = data.currency || 'INR'
      this.is24hOpen = data.is24hOpen || false
      this.isServiceChargePublic = data.is_service_charge_public || false

      // Populate custom amounts
      if (Array.isArray(data.payment_amounts) && data.payment_amounts.length > 0) {
        this.paymentAmounts = data.payment_amounts.map(val => {
          // Handle if value is object or primitive
          return { value: (val && typeof val === 'object' && val.value) ? val.value : val }
        })
      } else {
        this.paymentAmounts = [{ value: '' }]
      }
      this.allowCustomAmount = data.allow_custom_amount === true || data.allow_custom_amount === 'true'
      this.minCustomAmount = data.min_custom_amount || ''

      // Use platform fee from existing data
      this.platformFeeAmount = data.platform_fee_amount || 0
      // Calculate percentage from amount and total if possible, or fallback to service list
      if (this.serviceCharge > 0) {
        this.platformFeePercentage = ((this.platformFeeAmount / this.serviceCharge) * 100).toFixed(2)
        // Remove trailing .00 if integer
        this.platformFeePercentage = parseFloat(this.platformFeePercentage)
      } else {
        // Try to find from service list
        const selected = this.serviceList.find((s) => s.value === this.selectedServiceId)
        if (selected) {
          this.platformFeePercentage = selected.collection_platform_fee || 0
        }
      }

      this.calculateFees()

      // Load existing service image if available
      if (data.service_image) {
        this.existingServiceImage = data.service_image
        // Set preview to show existing image
        this.serviceImagePreview = data.service_image
      }

      // Step 3 data
      this.paymentCollection = data.collect_payments === 'AfterApproval' ? 'after_approval' : 'during_booking'

      // Rules and policies
      if (data.rules_and_policies) {
        this.infoSections.forEach(section => {
          const key = this.getRulesKey(section.title)
          if (data.rules_and_policies[key]) {
            section.value = data.rules_and_policies[key].text || ''
          }
        })
      }

      // Documents
      if (data.collect_documents && Array.isArray(data.collect_documents)) {
        this.documents = data.collect_documents.map(doc => ({
          name: doc.document_name,
          mandatory: doc.is_required,
          hint: doc.description,
          _editing: false,
          _dirty: false,
          _backup: null
        }))
      }
    },
    // Helper to map rules section titles to API keys
    getRulesKey(title) {
      const map = {
        'Payment Terms *': 'payment_policy',
        'Rules And Regulations *': 'rules_and_regulations',
        'Safety Information *': 'safety_information',
        'Cancellation Policy *': 'cancellation_policy',
        'Refund Policy *': 'refund_policy'
      }
      return map[title] || title.toLowerCase().replace(/\s+/g, '_')
    },
    async loadCurrencyOptions() {
      try {
        const response = await this.$http.get('admin/currencies')
        this.currencyOptions = (response.data && response.data.data) || []
      } catch (error) {
        console.warn('Failed to load currencies', error)
        this.currencyOptions = [
          { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
          { code: 'USD', name: 'US Dollar', symbol: '$' },
          { code: 'GBP', name: 'British Pound', symbol: '£' },
          { code: 'EUR', name: 'Euro', symbol: '€' }
        ]
      }
      if (!this.currencyOptions.some((c) => c.code === this.serviceCurrency)) {
        this.currencyOptions.unshift({
          code: this.serviceCurrency,
          name: this.serviceCurrency,
          symbol: getCurrencySymbol(this.serviceCurrency)
        })
      }
    },
    // Load services list for dropdown
    async loadServicesList() {
      this.servicesLoading = true
      try {
        const response = await this.$http.get('admin/services/list', { params: { limit: 500 } })

        let assignedServiceIds = []
        if (this.selectedGurudwaraId) {
          try {
            const assignedRes = await this.$http.get('admin/gurudwara-service/list', {
              params: { gurudwara: this.selectedGurudwaraId, limit: 500 }
            })
            const assigned = (assignedRes.data && assignedRes.data.data && assignedRes.data.data.results) || []
            assignedServiceIds = assigned.map((item) => {
              const serviceId = item.service && item.service._id ? item.service._id : item.service
              return String(serviceId)
            }).filter(Boolean)
          } catch (assignedError) {
            console.warn('Unable to load assigned gurudwara services:', assignedError)
          }
        }

        if (response.data && response.data.data && response.data.data.results) {
          const currentServiceId = this.selectedServiceId ? String(this.selectedServiceId) : null
          this.serviceList = response.data.data.results
            .filter((service) => {
              if (this.isEditMode && currentServiceId && String(service._id) === currentServiceId) {
                return true
              }
              return !assignedServiceIds.includes(String(service._id))
            })
            .map(service => ({
              value: service._id,
              label: service.service_name,
              collection_platform_fee: service.collection_platform_fee || 0,
              requires_booking_date: service.requires_booking_date !== false,
              requires_time_slot: service.requires_time_slot !== false
            }))

          // If we have a selected service, update the percentage now that we have the list
          if (this.selectedServiceId) {
            const selected = this.serviceList.find((s) => s.value === this.selectedServiceId)
            if (selected) {
              this.platformFeePercentage = selected.collection_platform_fee || 0
              // Force update received amount based on current service charge
              const charge = Number(this.serviceCharge) || 0
              if (charge > 0) {
                this.platformFeeAmount = calcPlatformFeeAmount(charge, this.platformFeePercentage)
                this.receivedAmount = calcNetReceive(charge, this.platformFeePercentage).toFixed(2)
              }
            }
          }
        } else {
          console.warn('Unexpected response structure:', response.data)
          // Fallback with test data
          // this.serviceList = [
          //   { value: 'test1', label: 'Test Service 1' },
          //   { value: 'test2', label: 'Test Service 2' }
          // ]
        }
      } catch (error) {
        console.error('Error loading services:', error)
        // Fallback with test data on error
        // this.serviceList = [
        //   { value: 'test1', label: 'Test Service 1' },
        //   { value: 'test2', label: 'Test Service 2' }
        // ]
        this.$vs && this.$vs.notify && this.$vs.notify({
          title: 'Error',
          text: 'Failed to load services list, using test data',
          color: 'warning',
          position: 'top-center'
        })
      } finally {
        this.servicesLoading = false
      }
    },
  },
  /** On Rendering */
  async mounted() {
    await this.loadCurrencyOptions()
    if (this.serviceCharge > 0) {
      this.receivedAmount = calcNetReceive(this.serviceCharge, this.platformFeePercentage).toFixed(2)
    }
  },
  components: {
    flatPickr,
    Select2,
    CurrencySelect,
    DocumentCollectionSection
  }
}
</script>

<style scoped>
.charge-currency-block {
  width: 100%;
}

.charge-currency-label {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.currency-amount-prefix {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  padding: 0 12px;
  min-height: 42px;
  border: 1px solid #d1d5db;
  border-right: none;
  border-radius: 4px 0 0 4px;
  background: #f3f4f6;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  flex-shrink: 0;
}

.currency-amount-prefix--compact {
  min-height: 38px;
  min-width: 42px;
  padding: 0 10px;
  font-size: 14px;
}

/* Service Form Wrapper */
.service-form-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

/* Service Form Container - Creates inner scrolling like vs-table */
.service-form-container {
  position: relative;
  width: 100%;
  max-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Scrollable Form Content */
.form-content-scrollable {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}


/* Sticky header */
.sticky-action-buttons {
  position: sticky;
  top: 0;
  z-index: 1000;
  background: #ffffff;
  padding: 16px 0px;
  flex-shrink: 0;
}

.sticky-buttons-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

/* Scrollable content area */
.service-form-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 18px 0px;
  margin: 0 auto;
  width: 100%;
}

/* Custom scrollbar */
.service-form-content::-webkit-scrollbar {
  width: 8px;
}

.service-form-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.service-form-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.service-form-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.service-name-display {
  font-size: 18px;
  font-weight: 600;
  color: #000000;
}

.form-content {
  padding: 24px;
  min-height: fit-content;
}

.service-charge-switch {
  background-color: #F0EFEE;
  border-radius: 10px;
  padding: 27px 20px;
  width: auto;
}

::v-deep .vs-con-textarea h4 {
  font-size: 1rem !important;
}

.service-location-card {
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  border-color: #e5e7eb;
  background-color: #ffffff;
  width: max-content;
  max-width: 100%;
}

.months-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}

.month-card {
  min-width: 0;
}

.option-cards-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
  width: 100%;
  max-width: 100%;
}

.option-choice-card {
  padding: 24px 20px;
  min-height: 180px;
  height: auto;
  box-sizing: border-box;
}

.option-choice-card--tall {
  min-height: 200px;
}

.settings-row {
  width: 100%;
  max-width: 720px;
}

.settings-input {
  width: 100%;
  max-width: 180px;
}

.days-hours-header {
  width: 100%;
  max-width: 100%;
}

.service-location-card.selected {
  border-color: rgba(var(--vs-primary), 1);
  background-color: rgba(var(--vs-primary), 0.08);
  box-shadow: 0 8px 18px rgba(var(--vs-primary), 0.12);
}

.service-location-checkbox {
  border: 1px solid #d1d5db;
  color: transparent;
}

.service-location-checkbox.selected {
  border-color: rgba(var(--vs-primary), 1);
  background-color: rgba(var(--vs-primary), 1);
  color: #ffffff;
}

.service-location-selected-label {
  color: rgba(var(--vs-primary), 1);
}

/* File Upload Styles - Matching SaraiForm */
.file-upload-wrapper {
  margin-bottom: 12px;
}

.file-upload-box {
  display: block;
  width: 100%;
  max-width: 400px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 24px 28px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #ffffff;
}

.file-upload-box:hover {
  border-color: #9ca3af;
  background-color: #f9fafb;
}

.file-input-hidden {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
  z-index: -1;
}

.file-upload-content-new {
  display: flex;
  align-items: center;
  gap: 12px;
}

.file-upload-text-new {
  font-size: 16px;
  color: #000000;
  font-weight: 500;
}

.service-image-preview {
  position: relative;
  width: 35%;
  margin-top: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f9fafb;
  padding: 10px;
}

.service-image-preview img {
  width: 100%;
  height: auto;
  display: block;
  max-height: 300px;
  object-fit: contain;
}


.remove-image-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.7);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #ffffff;
  transition: background-color 0.2s ease;
}

.remove-image-btn:hover {
  background-color: rgba(0, 0, 0, 0.9);
}

.file-upload-note {
  background-color: #FFEE9A;
  border-radius: 4px;
  padding: 5px 10px;
  margin-top: 12px;
}

.file-upload-note-text {
  font-size: 17px;
  color: #933F05;
  margin: 0;
  line-height: 100%;
}

/* Sticky Action Buttons Styling */
.cancel-btn {
  font-size: 16px !important;
  border: 1px solid #cccccce5 !important;
  padding: 0px 35px !important;
  height: 60px !important;
  border-radius: 16px !important;
  transition: all 0.2s ease;
}

.save-draft-btn {
  background-color: #f0efee;
  border: 1px solid #f0efee !important;
  border-radius: 18px !important;
  padding: 0px 70px !important;
  height: 60px !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  width: 280px !important;
  color: black;
  transition: all 0.2s ease;
}

.publish-btn {
  background: #000000 !important;
  color: #ffffff !important;
  border-radius: 18px !important;
  padding: 0px 100px !important;
  height: 60px !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  width: 280px;
  transition: all 0.2s ease;
}

::v-deep .save-draft-btn:disabled,
::v-deep .publish-btn:disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}


/* Operating Days & Hours Styles */
.operating-days-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 100%;
  width: 100%;
}

.day-row-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
  min-width: 0;
}

.operating-day-card {
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  overflow: visible;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.operating-day-card.has-error {
  border: 2px solid #ef4444;
}

.day-card-body {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  min-width: 0;
}

.day-card-main {
  flex: 1;
  min-width: 0;
}

.interval-row-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 24px;
}

.day-header {
  margin-bottom: 0;
  max-width: 200px;
  width: 200px;
  flex-shrink: 0;
}

.day-toggle-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.day-label {
  font-weight: 400;
  font-size: 20px;
  color: #000000;
  margin: 0;
}


.unavailable-text {
  color: #000000;
  font-weight: 700;
  font-size: 20px;
  margin: 0;
}

.day-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.intervals-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.interval-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.time-input-wrapper {
  flex: 0 0 auto;
  min-width: 120px;
  width: 100%;
  max-width: 150px;
}

.time-input {
  width: 100%;
}

.time-separator {
  color: #6b7280;
  font-weight: 500;
  font-size: 16px;
  margin: 0 4px;
}

.flatpickr-input[readonly] {
  background-color: #D0DBEE !important;
}

/* Light blue time input styling - Target all possible flatpickr input structures */
::v-deep .time-input-wrapper input,
::v-deep .time-input-wrapper input.flatpickr-input,
::v-deep .time-input-wrapper .flatpickr-input,
::v-deep .time-input-wrapper input[type="text"],
::v-deep .time-input-wrapper .flatpickr-wrapper input,
::v-deep .time-input-wrapper .flatpickr-wrapper input.flatpickr-input,
::v-deep .time-input input,
::v-deep .time-input input.flatpickr-input,
::v-deep .time-input .flatpickr-input,
::v-deep .time-input input[type="text"],
::v-deep .time-input .flatpickr-wrapper input,
::v-deep .time-input .flatpickr-wrapper input.flatpickr-input,
::v-deep .time-input-wrapper .flatpickr-wrapper,
::v-deep .time-input-wrapper .flatpickr-wrapper>input,
::v-deep .time-input .flatpickr-wrapper>input {
  border: 1px solid #bae6fd !important;
  border-radius: 10px !important;
  padding: 8px 12px !important;
  color: black !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  width: 100% !important;
  box-sizing: border-box !important;
}

::v-deep .time-input-wrapper input:focus,
::v-deep .time-input-wrapper input.flatpickr-input:focus,
::v-deep .time-input-wrapper .flatpickr-input:focus,
::v-deep .time-input-wrapper input[type="text"]:focus,
::v-deep .time-input-wrapper .flatpickr-wrapper input:focus,
::v-deep .time-input-wrapper .flatpickr-wrapper input.flatpickr-input:focus,
::v-deep .time-input input:focus,
::v-deep .time-input input.flatpickr-input:focus,
::v-deep .time-input .flatpickr-input:focus,
::v-deep .time-input input[type="text"]:focus,
::v-deep .time-input .flatpickr-wrapper input:focus,
::v-deep .time-input .flatpickr-wrapper input.flatpickr-input:focus {
  outline: none !important;
  border-color: #7dd3fc !important;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1) !important;
}

::v-deep .time-input-wrapper input::placeholder,
::v-deep .time-input-wrapper input.flatpickr-input::placeholder,
::v-deep .time-input-wrapper .flatpickr-input::placeholder,
::v-deep .time-input-wrapper input[type="text"]::placeholder,
::v-deep .time-input-wrapper .flatpickr-wrapper input::placeholder,
::v-deep .time-input-wrapper .flatpickr-wrapper input.flatpickr-input::placeholder,
::v-deep .time-input input::placeholder,
::v-deep .time-input input.flatpickr-input::placeholder,
::v-deep .time-input .flatpickr-input::placeholder,
::v-deep .time-input input[type="text"]::placeholder,
::v-deep .time-input .flatpickr-wrapper input::placeholder,
::v-deep .time-input .flatpickr-wrapper input.flatpickr-input::placeholder {
  color: black !important;
  opacity: 1 !important;
}

/* Target flatpickr wrapper to ensure proper width */
::v-deep .time-input-wrapper .flatpickr-wrapper,
::v-deep .time-input-wrapper>div,
::v-deep .time-input .flatpickr-wrapper,
::v-deep .time-input>div {
  width: 100% !important;
  display: block !important;
}

/* Additional selector to ensure background color applies - target any input inside time-input-wrapper */
::v-deep .time-input-wrapper input[type="text"],
::v-deep .time-input-wrapper .flatpickr-wrapper input[type="text"],
::v-deep .time-input-wrapper .flatpickr-input[type="text"],
::v-deep .time-input-wrapper * input,
::v-deep .time-input-wrapper .flatpickr-wrapper .flatpickr-input,
::v-deep .time-input-wrapper .flatpickr-wrapper input.flatpickr-input {
  border: 1px solid #bae6fd !important;
  border-radius: 10px !important;
  color: black !important;
}

/* Most specific selector to override any other styles */
::v-deep .time-input-wrapper .flatpickr-wrapper input.flatpickr-input[type="text"],
::v-deep .time-input-wrapper .flatpickr-wrapper>input.flatpickr-input {
  border: 1px solid #bae6fd !important;
  border-radius: 10px !important;
  color: black !important;
}

.add-interval-link {
  color: #0B1444;
  font-size: 18px;
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.2s ease;
  white-space: nowrap;
}

.add-interval-link:hover {
  opacity: 0.8;
}

.remove-interval-btn {
  background-color: transparent;
  color: #0B1444;
  border: none;
  padding: 0;
  font-size: 18px;
  cursor: pointer;
  gap: 5px;
  transition: opacity 0.2s ease;
  white-space: nowrap;
}

.remove-interval-btn:hover {
  opacity: 0.8;
}

.validation-messages {
  margin-top: 8px;
}

.success-message-box {
  display: flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  background-color: #D8F9D8;
  color: #021C02;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
}

.success-message-box .success-icon {
  color: #021C02;
  flex-shrink: 0;
}

.error-message-box {
  display: flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  background-color: #FFF0F0;
  color: #400404;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 17px;
}

.error-message-box .error-icon {
  color: #400404;
  flex-shrink: 0;
}

.copy-section-outside {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
  min-width: 160px;
  max-width: 200px;
  padding-top: 16px;
  justify-content: center;
  align-self: center;
}

.copy-to-all-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: transparent;
  border: none;
  padding: 0;
  margin: 0;
  font-size: 18px;
  cursor: pointer;
  transition: opacity 0.2s ease;
  text-decoration: none;
  line-height: 100%;
  color: #0B1444;
}

.copy-to-all-btn:hover:not(:disabled) {
  opacity: 0.8;
}

.copy-to-all-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.copy-to-all-btn svg {
  flex-shrink: 0;
}

.copy-error-message {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #400404;
  font-size: 18px;
  line-height: 1.5;
  min-height: 24px;
  margin: 0;
  padding: 0;
}

.copy-error-message svg {
  color: #400404;
  flex-shrink: 0;
}

/* Laptop / small desktop — matches typical Gurudwara admin viewports */
@media (max-width: 1400px) {
  .operating-days-container {
    max-width: 100%;
  }

  .day-row-wrapper {
    gap: 16px;
  }

  .copy-section-outside {
    min-width: 140px;
  }

  .day-header {
    width: 140px;
    max-width: 140px;
  }

  .day-label,
  .unavailable-text {
    font-size: 16px;
  }

  .save-draft-btn,
  .publish-btn {
    width: auto !important;
    max-width: 220px;
    padding: 0 32px !important;
  }
}

@media (max-width: 1250px) {
  .option-cards-grid {
    grid-template-columns: 1fr;
  }

  .option-choice-card,
  .option-choice-card--tall {
    min-height: 0;
  }

  .day-row-wrapper {
    flex-direction: column;
    align-items: stretch;
  }

  .copy-section-outside {
    align-self: flex-start;
    margin-top: 0;
    padding-top: 0;
    min-width: 0;
    max-width: none;
  }

  .day-card-body {
    flex-direction: column;
    align-items: stretch;
  }

  .day-header {
    width: 100%;
    max-width: none;
    margin-bottom: 8px;
  }

  .settings-row {
    max-width: 100%;
  }

  .settings-input {
    max-width: 220px;
  }

  .service-image-preview {
    width: 100%;
    max-width: 320px;
  }

  .form-content {
    padding: 16px;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .operating-days-container {
    max-width: 100%;
  }

  .day-row-wrapper {
    flex-direction: column;
    align-items: stretch;
  }

  .copy-section-outside {
    align-self: flex-start;
    margin-top: 8px;
  }

  .interval-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .time-input-wrapper {
    width: 100%;
    max-width: 100%;
  }

  .months-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }

  .settings-input {
    max-width: 100%;
  }
}

/* Tooltip Styles */
.tooltip-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.tooltip-text {
  visibility: hidden;
  opacity: 0;
  background-color: #1f2937;
  color: #ffffff;
  text-align: center;
  border-radius: 6px;
  padding: 8px 12px;
  position: absolute;
  z-index: 1000;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: opacity 0.3s ease, visibility 0.3s ease;
  pointer-events: none;
}

.tooltip-text::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #1f2937 transparent transparent transparent;
}

.tooltip-wrapper:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

/* For better positioning on smaller screens */
@media (max-width: 768px) {
  .tooltip-text {
    white-space: normal;
    max-width: 250px;
    word-wrap: break-word;
  }
}

/* Service Form Responsive Font Sizes */
.service-heading-primary {
  font-size: 24px;
}

.service-heading-secondary {
  font-size: 20px;
}

.service-form-label {
  font-size: 18px;
}

.service-text-base {
  font-size: 16px;
}

@media (max-width: 1024px) {

  .save-draft-btn,
  .publish-btn {
    width: auto !important;
    padding: 0 40px !important;
  }
}

@media (max-width: 768px) {
  .sticky-buttons-container {
    flex-direction: row;
    gap: 8px;
    align-items: center;
  }

  .sticky-buttons-container>div {
    width: auto;
  }

  .cancel-btn {
    padding: 0 15px !important;
    height: 48px !important;
    font-size: 14px !important;
  }

  .save-draft-btn,
  .publish-btn {
    height: 48px !important;
    padding: 0 15px !important;
    font-size: 14px !important;
    width: auto !important;
    min-width: 100px;
  }

  .service-form-content {
    padding: 12px;
  }

  .service-heading-primary {
    font-size: 20px;
  }
}

@media (max-width: 480px) {
  .sticky-buttons-container {
    padding: 0 8px;
  }

  .service-name-display {
    display: none;
  }
}

@media (max-width: 1400px) {
  .service-heading-primary {
    font-size: 20px;
  }

  .service-heading-secondary {
    font-size: 18px;
  }

  .service-form-label {
    font-size: 16px;
  }

  .service-text-base {
    font-size: 14px;
  }
}
</style>
